// EnhancedSafeAreaProvider.js
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { NavigationBarManager } from './NavigationBarManager';

const SafeAreaContext = createContext({
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  isNavigationBarHidden: false
});

export const useSafeArea = () => useContext(SafeAreaContext);

export const SafeAreaProvider = ({ children }) => {
  const [state, setState] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    isNavigationBarHidden: false
  });

  const updateSafeArea = useCallback(() => {
    const style = getComputedStyle(document.documentElement);
    const top = parseInt(style.getPropertyValue('--safe-area-top') || '0px') || 0;
    const bottom = parseInt(style.getPropertyValue('--safe-area-bottom') || '0px') || 0;
    const left = parseInt(style.getPropertyValue('--safe-area-left') || '0px') || 0;
    const right = parseInt(style.getPropertyValue('--safe-area-right') || '0px') || 0;
    setState({ top, bottom, left, right, isNavigationBarHidden: true });
  }, []);

  const setupSafeArea = useCallback(async () => {
    try {
      await NavigationBarManager.initialize();

      let info = null;
      try {
        const mod = await import(/* webpackIgnore: true */ '@capacitor/status-bar');
        info = await mod.StatusBar.getInfo();
      } catch (e) {
        console.log('StatusBar plugin not available, using CSS env() fallback');
      }

      let topInset = 0;
      let bottomInset = 0;
      let leftInset = 0;
      let rightInset = 0;

      if (info) {
        if (Capacitor.getPlatform() === 'android') {
          topInset = info.visible ? 24 : 0;
          bottomInset = info.visible ? 0 : 0;
        } else if (Capacitor.getPlatform() === 'ios') {
          topInset = info.visible ? 44 : 0;
          bottomInset = 34;
        }
      }

      const statusBarInset = info && Capacitor.getPlatform() === 'android' && info.visible
        ? `${topInset}px`
        : 'env(safe-area-inset-top, 0px)';
      const navBarInset = info && Capacitor.getPlatform() === 'ios'
        ? `${bottomInset}px`
        : 'env(safe-area-inset-bottom, 0px)';

      document.documentElement.style.setProperty('--safe-area-top', statusBarInset);
      document.documentElement.style.setProperty('--safe-area-bottom', navBarInset);
      document.documentElement.style.setProperty('--safe-area-left', leftInset ? `${leftInset}px` : 'env(safe-area-inset-left, 0px)');
      document.documentElement.style.setProperty('--safe-area-right', rightInset ? `${rightInset}px` : 'env(safe-area-inset-right, 0px)');

      updateSafeArea();

    } catch (error) {
      console.log('Safe area setup failed, using CSS env() fallback:', error);
      updateSafeArea();
    }
  }, [updateSafeArea]);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      setupSafeArea();
      window.addEventListener('resize', updateSafeArea);
      window.addEventListener('orientationchange', () => {
        setTimeout(updateSafeArea, 200);
      });
    }
    return () => {
      window.removeEventListener('resize', updateSafeArea);
    };
  }, [setupSafeArea, updateSafeArea]);

  return (
    <SafeAreaContext.Provider value={state}>
      {children}
    </SafeAreaContext.Provider>
  );
};
